import React, { FC, ReactNode, memo, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Typography from '@mui/material/Typography';
import Theme from '@src/theme';

import { useCreateProposal } from '@services/api/voting/common.api';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import { useActiveVoteFarmAction } from '@hooks/vaults/useActiveVoteFarmAction';
import { useVotingFarmAction } from '@hooks/vaults/useVotingFarmAction';

import { formatDateTime } from '@utils/date.utils';
import { nFormatter, toBN } from '@utils/formaters.utils';
import {
  getDexScreenerChainIcon,
  getDexScreenerDexIcon,
} from '@utils/logo.utils';

import { requestFarmYup } from '@validators/voting/common.validators';

import Box from '@components/common/Box';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SkeletonLoader from '@components/common/SkeletonLoader';
import ItemStats from '@components/vaults/ItemStats';
import DexScreenerTokenIcon from '@components/voting/DexScreenerTokenIcon';

import { DATE_FORMAT } from '@constants/common/date.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ErrorMessages } from '@constants/common/messages.constants';
import { CHAIN_FILTER_PARAM } from '@constants/common/params.constants';
import { PATH_CREATE_VAULT } from '@constants/common/routes.constants';
import { Variant } from '@constants/common/ui.constants';
import { FARM_IDs_PARAM } from '@constants/vaults/params.constants';
import {
  ADD_REQUEST_BASIC_STATS,
  ADD_REQUEST_STATS,
  DEXSCREENER_CHAIN_ID_MAP,
  DexscreenerChainId,
  FARM_ERROR_TEXT_MAP,
} from '@constants/voting/common.constants';

import { VotingFarmStatus } from '@dto/vaults/voting.dto';

import useStyles from './styles.module';

export type FormValues = {
  [FieldNames.Link]: string;
};

const scheme = yup.object().shape({
  [FieldNames.Link]: requestFarmYup,
});

const AddRequestModal: FC = () => {
  const { classes } = useStyles();
  const { account } = useAuth();
  const navigate = useNavigate();

  const {
    control,
    watch,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(scheme),
    defaultValues: { [FieldNames.Link]: '' },
  });

  const link = watch(FieldNames.Link);

  const {
    farmData,
    voteFarm,
    error: votingFarmActionError,
    isPending: isPendingFarmData,
  } = useVotingFarmAction({
    link,
    isValid,
  });

  const { mutateAsync, isPending } = useCreateProposal();
  const { activateVote, isPending: isPendingVote } = useActiveVoteFarmAction();

  const showError = useMemo(
    () => !!link && !!farmData && !!FARM_ERROR_TEXT_MAP[farmData.status],
    [farmData, link],
  );

  const errorInputMessage = useMemo(
    () =>
      (farmData?.status === VotingFarmStatus.BlackList
        ? ErrorMessages.FarmBlacklisted
        : '') || votingFarmActionError,
    [farmData, votingFarmActionError],
  );

  const stats: Record<string, string | ReactNode> = useMemo(() => {
    if (!farmData) {
      return {
        name: '-',
        protocol: '-',
        creationDate: '-',
        closing: '-',
        chain: '-',
        volume: '-',
        marketCap: '-',
        tvl: '-',
      };
    }

    const {
      dexscreenerChainId,
      pairSymbol,
      tvlUsd,
      createdAt,
      closingDate,
      dexName,
      marketCap,
      token0,
    } = farmData;

    const farmInfo = {
      name: (
        <span className={classes.contentBox}>
          <DexScreenerTokenIcon
            address={token0}
            dexscreenerChainId={dexscreenerChainId}
          />
          {pairSymbol}
        </span>
      ),
      protocol: (
        <span className={classes.contentBox}>
          <img src={getDexScreenerDexIcon(dexName)} alt="dex" />
          {dexName}
        </span>
      ),
      chain: (
        <span className={classes.contentBox}>
          <img
            src={getDexScreenerChainIcon(dexscreenerChainId)}
            alt="chainId"
          />
          {dexscreenerChainId}
        </span>
      ),
      volume: `$${nFormatter(toBN(tvlUsd), 2)}`,
      tvl: `$${nFormatter(toBN(tvlUsd), 2)}`,
      marketCap: `$${nFormatter(toBN(marketCap), 2)}`,
    };

    if (farmData?.status !== VotingFarmStatus.BlackList) {
      return {
        ...farmInfo,
        creationDate: formatDateTime(
          createdAt,
          DATE_FORMAT.DOT_MONTH_DATE_YEAR_WITH_TIME,
        ),
        closing: formatDateTime(
          closingDate,
          DATE_FORMAT.DOT_MONTH_DATE_YEAR_WITH_TIME,
        ),
      };
    }

    return farmInfo;
  }, [farmData]);

  const disabledCreateProposal = useMemo(
    () =>
      !isValid ||
      !farmData ||
      farmData?.isVoted ||
      farmData?.status === VotingFarmStatus.BlackList,
    [isValid, farmData],
  );

  const isProposalAllowed = useMemo(
    () =>
      !farmData ||
      [
        VotingFarmStatus.Preparing,
        VotingFarmStatus.Declined,
        VotingFarmStatus.BlackList,
      ].includes(farmData.status),
    [farmData],
  );

  const isVaultCreationAllowed = useMemo(
    () => farmData?.status === VotingFarmStatus.Added,
    [farmData],
  );

  const isVotingAllowed = useMemo(
    () => farmData?.status === VotingFarmStatus.Active,
    [farmData],
  );

  const createProposal = useCallback(async () => {
    try {
      if (!account || !farmData?.id) return;
      await mutateAsync({ account, id: farmData.id });
      await voteFarm(link);
    } catch (e) {
      console.error('createProposal', e);
    }
  }, [account, link, farmData, reset, mutateAsync]);

  const createVault = useCallback(() => {
    if (!farmData || !farmData?.farmId) return;
    const chaidId =
      DEXSCREENER_CHAIN_ID_MAP[
        farmData.dexscreenerChainId as DexscreenerChainId
      ];

    if (chaidId) {
      const queryParams = `${CHAIN_FILTER_PARAM}=${encodeURIComponent(JSON.stringify([chaidId]))}&${FARM_IDs_PARAM}=${[farmData.farmId].join(',')}`;
      modalActions.hide();
      navigate(`${PATH_CREATE_VAULT}/?${queryParams}`);
    }

    modalActions.hide();
    navigate(PATH_CREATE_VAULT);
  }, [farmData, navigate]);

  const vote = useCallback(async () => {
    if (!farmData?.id) return;
    await activateVote(farmData.id);
    reset();
    modalActions.hide();
  }, [farmData, reset, activateVote]);

  return (
    <ModalWrapper title="Add">
      <div className={classes.childrenBox}>
        <div className={classes.content}>
          <div>
            <Typography
              variant="body1"
              color={(theme) => theme.secondary.s3}
              marginBottom="8px"
            >
              Paste a Dex Screener link to auto-fill farm details
            </Typography>
            <Controller
              control={control}
              name={FieldNames.Link}
              render={({
                field: { value, onBlur, onChange },
                fieldState: { error: errorObj },
              }) => (
                <Input
                  isDark
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  inputError={errorObj?.message || errorInputMessage}
                  placeholder="https://dexscreener.com/..."
                />
              )}
            />
          </div>
          <Box classNameChildren={classes.stats}>
            {(farmData?.status !== VotingFarmStatus.BlackList
              ? ADD_REQUEST_STATS
              : ADD_REQUEST_BASIC_STATS
            ).map(({ keyValue, title, ...rest }) => {
              return (
                <ItemStats
                  key={keyValue}
                  title={title}
                  value={
                    isPendingFarmData ? (
                      <div key={title} className={classes.loaderBox}>
                        <SkeletonLoader className={classes.loader} />
                      </div>
                    ) : (
                      stats[keyValue]
                    )
                  }
                  {...rest}
                  titleVariant="body2"
                  valueVariant="body2"
                  titleColor={Theme.primary.p2}
                  className={classes.item}
                  classNameValue={classes.value}
                  classNameTitle={classes.title}
                />
              );
            })}
          </Box>
        </div>

        {showError && !!farmData && (
          <div className={classes.warningBox}>
            <HelpOutlineIcon className={classes.icon} />
            <Typography variant="subtitle1" color={(color) => color.accent.a4}>
              {FARM_ERROR_TEXT_MAP[farmData?.status]}
            </Typography>
          </div>
        )}

        <div className={classes.btns}>
          <Button variant={Variant.Secondary} onClick={modalActions.hide}>
            Cancel
          </Button>

          {isVaultCreationAllowed && (
            <Button
              checkAuth
              variant={Variant.Gradient}
              onClick={createVault}
              disabled={!isValid}
            >
              Create vault
            </Button>
          )}

          {isProposalAllowed && (
            <Button
              variant={Variant.Gradient}
              checkAuth
              disabled={disabledCreateProposal}
              isLoading={isPending}
              onClick={createProposal}
            >
              Create Proposal
            </Button>
          )}
          {isVotingAllowed && (
            <Button
              variant={Variant.Gradient}
              disabled={farmData?.isVoted || !isValid}
              onClick={vote}
              checkAuth
              isLoading={isPendingVote}
            >
              {farmData?.isVoted ? 'Voted' : 'Vote'}
            </Button>
          )}
        </div>
      </div>
    </ModalWrapper>
  );
};

export default memo(AddRequestModal);
